import { alpha, Box, Chip, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Checklist3,
  CustomiseTabs,
  Switch,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import React, { useState } from 'react';
import SVGScale from '../../atoms/Icons/Scale';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import SVGMedicalFolder from '../../atoms/Icons/MedicalFolder';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import AppCard from '../../atoms/AppCard';
import DiscoverTune from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Icons/Discover_tune';
import BasicModal from '../../atoms/BasicModal';
import { TabItem } from '@HomaHealth/ui-component-library/lib/_AIQ-components/organisms/CustomiseTabs';

export interface QuickLinkItem {
  TITLE: string;
  IMAGE_E: string;
  IMAGE_D: string;
  DISABLED: boolean;
  LINK: string;
  DIRECTLINK: boolean;
  quickLinkvisibility: boolean;
  quickLinkIndex: number;
  SCREEN_NAME: string;
}
export interface QuickLinkGroup {
  title: string;
  links: QuickLinkItem[];
}
export interface UserQuickLinkOverride {
  quickLinkName: string;
  quickLinkvisibility: boolean;
  quickLinkIndex: number;
}

export interface HomeQuickLinksProps {
  activeChips: number[];
  setActiveChips: React.Dispatch<React.SetStateAction<number[]>>;
   
  handleCardClick: (item: QuickLinkItem) => void;
  userLinks: QuickLinkGroup[];
  tabs: TabItem[];
  setTabs: React.Dispatch<React.SetStateAction<TabItem[]>>;
  handleUpdateQuickLinks: () => void;
}

const HomeQuickLinks: React.FC<HomeQuickLinksProps> = ({
  activeChips,
  setActiveChips,
  handleCardClick,
  userLinks,
  tabs,
  setTabs,
  handleUpdateQuickLinks,
}) => {
  const { classes } = makeStyles()({
    root: {
      marginTop: '1rem',
      width: '100%',
    },
    flex: {
      display: 'flex',
      alignItems: 'center',
    },
    topContainer: {
      justifyContent: 'space-between',
    },
    titleContainer: {
      gap: '2rem',
    },
    chipsContainer: {
      gap: '1rem',
    },
    chip: {
      borderRadius: '16px',
      fontSize: '0.75rem',
      color: patientAccessTheme.palette.text.mediumemphasis,
      height: '28px',
      padding: '0 0.25rem',
      cursor: 'pointer',
    },
    inactiveIcon: {
      '& .MuiChip-icon': {
        '& path': {
          fill: alpha('#000', 0.5),
        },
      },
    },
    quickLinkContainer: {
      marginTop: '1.5rem',
      gap: '1rem',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    },
  })();

  const links = [
    {
      label: 'Plan Management',
      icon: <Checklist3 height="18px" width="18px" />,
      color: '#5478D6',
    },
    {
      label: 'Contract AIQ',
      icon: <SVGScale height="18px" width="18px" />,
      color: '#D6545E',
    },
    {
      label: 'Claims AIQ',
      icon: <SVGMedicalFolder height="18px" width="18px" />,
      color: '#17A39C',
    },
  ];

  const [isOpenQuickLinksModal, setIsOpenQuickLinksModal] = useState<boolean>(
    false,
  );

  const handleChipClick = (index: number) => {
    setActiveChips((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const getAllActiveLinks = () => {
    const allLinks = userLinks.flatMap((group) => group.links);

    let activeLinks: QuickLinkItem[];

    if (activeChips.length === 0) {
      activeLinks = allLinks.filter((item) => item.quickLinkvisibility);
    } else {
      activeLinks = activeChips.flatMap((chipIndex) =>
        chipIndex >= 0 && chipIndex < userLinks.length
          ? userLinks[chipIndex].links.filter(
              (item) => item.quickLinkvisibility,
            )
          : [],
      );
    }
    return activeLinks.sort((a, b) => a.quickLinkIndex - b.quickLinkIndex);
  };

  const handleModalClose = () => {
    setIsOpenQuickLinksModal(false);
  };
  const handleItemAction = (id: string, actionType: string, value?: any) => {
    if (actionType === 'toggle-visibility') {
      setTabs((prev) =>
        prev.map((t) => (t.id === id ? { ...t, visible: value } : t)),
      );
    }

    if (actionType === 'reorder' && Array.isArray(value)) {
      setTabs(value);
    }
  };

  const renderVisibilityControl = (tab: any, onAction: any) => (
    <Switch
      checked={tab.visible}
      color="primary"
      handleChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        onAction('toggle-visibility', e.target.checked)
      }
    />
  );
  return (
    <Box className={classes.root}>
      <Grid className={`${classes.flex} ${classes.topContainer}`}>
        <Grid className={`${classes.flex} ${classes.titleContainer}`}>
          <Typography fontType="semiBold">Quick Links</Typography>
          <Grid className={`${classes.flex} ${classes.chipsContainer}`}>
            {links.map((link, i) => {
              const isActive = activeChips.includes(i);
              return (
                <Chip
                  key={i}
                  icon={link.icon}
                  label={link.label}
                  variant="outlined"
                  onClick={() => handleChipClick(i)}
                  className={`${classes.chip} ${
                    !isActive && classes.inactiveIcon
                  }`}
                  style={{
                    backgroundColor: isActive
                      ? alpha(link.color, 0.25)
                      : alpha('#000', 0.05),
                    borderColor: isActive
                      ? alpha(link.color, 0.5)
                      : alpha('#000', 0.1),
                  }}
                />
              );
            })}
          </Grid>
        </Grid>
        <Grid>
          <DiscoverTune
            style={{ cursor: 'pointer' }}
            onClick={() => setIsOpenQuickLinksModal(true)}
          />
        </Grid>
      </Grid>
      <Grid className={classes.quickLinkContainer}>
        {getAllActiveLinks().map((item, index) => (
          <AppCard
            title={item.TITLE}
            tooltip={item.TITLE}
            image={item.IMAGE_E}
            disabled={item.DISABLED}
            onClick={() => handleCardClick(item)}
            key={index}
          />
        ))}
      </Grid>
      {isOpenQuickLinksModal && (
        <BasicModal
          open={isOpenQuickLinksModal}
          handleClose={handleModalClose}
          title={'Quick Link'}
          isSaveButton={true}
          saveBtnText="Save Configuration"
          saveBtnAction={() => {
            handleUpdateQuickLinks();
            handleModalClose();
          }}
          isScrollable={true}
          isCloseBtn={true}
          width="auto"
        >
          <CustomiseTabs
            open={true}
            title1="Arrange and select quick links"
            title2="Visibility"
            tabs={tabs}
            renderItemAction={renderVisibilityControl}
            onItemAction={handleItemAction}
            width="100%"
            setTabs={setTabs}
          />
        </BasicModal>
      )}
    </Box>
  );
};

export default HomeQuickLinks;
